import {put, call} from 'redux-saga/effects';
import Lead from "../../entities/lead";
import * as Actions from "../actions/leadActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type LeadAction = {
    leadId: string;
    lead: Lead;
    leads: Lead[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneLead(action: LeadAction) {
	const session = tabSession.captureGeneration();
	try {
		const lead: Lead = yield call(api.findOne, 'lead', action.leadId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.LEAD_FETCH_ONE_SUCCESS, leadId: action.leadId, lead});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.LEAD_FETCH_ONE_FAILURE, leadId: action.leadId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {LeadAction} action
 */
export function* saveLead(action: LeadAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ LEAD_SAVE saga', action.lead?.id);
    
	if (action.lead === undefined || action.lead === null) {
		console.error('saveLead received a null');
		throw new Error('saveLead received a null');
	}

	if (!(action.lead instanceof Lead)) {
		action.lead = new Lead(action.lead);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'lead', action.lead);
		if (addResult.isErr()) {
			console.error(`⮂ saveLead: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.LEAD_SAVE_FAILURE, lead: action.lead, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.LEAD_SAVE, lead: action.lead});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.LEAD_SAVE_FAILURE, lead: action.lead, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {LeadAction} action
 * @param {Lead[]} action.leads
 */
export function* saveManyLeads(action: LeadAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ LEAD_SAVE_MANY saga', action.leads.map(lead => lead.id).join(','));
    
    if (!Array.isArray(action.leads)) {
        console.error('saveManyLeads expected an array, received', action.leads);
        throw new Error('saveManyLeads expected an array');
    }

	try {
		const toSave: Lead[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.leads.length; i++) {
			const lead = action.leads[i];
			lead.persisted = false;
			toSave.push(lead);
		}

		const addResult = yield call(idb.add, 'lead', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyLeads: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.LEAD_SAVE_MANY_FAILURE, leads: action.leads, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.LEAD_SAVE_MANY, leads: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.LEAD_SAVE_MANY_FAILURE, leads: action.leads, status: 500, err});

		console.error(`Error saving ${action.leads.length} leads to database`, err);
	}
}

/**
 * @param {LeadAction} action
 */
export function* removeLead(action: LeadAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.lead === undefined || action.lead === null || !(action.lead instanceof Lead)) {
			console.error('removeLead expected type Lead, received', action.lead);
			throw new Error('removeLead expected type Lead');
		}

	try {
		// Mark as deleted
		action.lead.status = 'D';
		action.lead.addedOn = newHRTimestamp();
		action.lead.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'lead', action.lead);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeLead: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.LEAD_REMOVE_FAILURE, lead: action.lead, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.LEAD_REMOVE, lead: action.lead});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.LEAD_REMOVE_FAILURE, lead: action.lead, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Lead.
	*
	* @param {LeadAction} action
	*/
export function* restoreLead(action: LeadAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreLead expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.lead === undefined || action.lead === null) {
        console.error('restoreLead expected a Lead, received null');
        throw new Error('restoreLead expected a Lead, received null');
    }
    else if (!(action.lead instanceof Lead)) action.lead = new Lead(action.lead); 
    
	try {
		// Mark as restored
		action.lead.status = 'U';
		action.lead.addedOn = newHRTimestamp();
		action.lead.addedBy = userId;
		action.lead.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'lead', action.lead);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreLead: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.LEAD_RESTORE_FAILURE, lead: action.lead, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.LEAD_RESTORE, lead: action.lead});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.LEAD_RESTORE_FAILURE, lead: action.lead, status: 500, err});

		console.error('Error restoring from database', err);
	}
}